import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getPipelineStatus } from '../api';
import { useAuth } from './AuthContext';

const PipelineContext = createContext(null);

// Default shape when status hasn't loaded yet or user isn't logged in.
// Pipeline defaults to OFF — components show the simplified direct-pay flow.
const DEFAULT_STATUS = {
  pipeline_enabled: false,
  enabledStages: ['pending', 'paid'],
  approvalEnabled: false,
  invoicingEnabled: false,
  payrollReadyEnabled: false,
};

export function PipelineProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(DEFAULT_STATUS);
      setLoaded(true);
      return;
    }
    try {
      const res = await getPipelineStatus();
      setStatus(res.data);
    } catch (err) {
      // On error, fall back to pipeline-off default so UI renders simplified flow
      setStatus(DEFAULT_STATUS);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    ...status,
    loaded,
    refresh,
  }), [status, loaded, refresh]);

  return (
    <PipelineContext.Provider value={value}>
      {children}
    </PipelineContext.Provider>
  );
}

export function usePipeline() {
  const ctx = useContext(PipelineContext);
  if (!ctx) throw new Error('usePipeline must be used within a PipelineProvider');
  return ctx;
}
